<template>
  <PForm
    ref="pForm"
    :heading="`${formState.view ? 'View' : 'Add'} E-invoice`"
    :primary-cta="submitButton"
    :secondary-cta="secondaryCta"
    @close="closeForm"
    @reset="resetForm"
    @submit.prevent="onSubmit"
  >
    <template #header-info>
      <PBtn v-if="showAiSuggestButton" :fluid="false" class="q-mr-xl" ai>
        <AiStarsIcon class="icon--sm q-mr-xxs" />
        <span>AI Suggest</span>
        <PMenu anchor="bottom left" self="top left">
          <div
            class="q-pa-2x-md"
            v-html="formData?.pod?.ai_verification?.reason"
          ></div>
        </PMenu>
      </PBtn>
    </template>

    <div
      v-if="unref(shortFallAmount) > 0"
      class="q-pa-md q-mb-md flex items-center bg-warning-20 rounded-borders"
    >
      <ExclamationIcon class="icon--xs exclamation__icon" />
      <span class="text-xs text-neutral q-mx-xs">
        Invoice amount is more than available credit limit.
        <strong
          >Shortfall
          {{
            numberFormat({
              number: shortFallAmount,
              style: "currency",
            })
          }}</strong
        >
      </span>

      <PBtn
        flat
        :disable="isRequestIncreaseButtonDisabled"
        dense
        color="primary"
        class="text-primary text-regular q-ml-xs"
        @click="handleRequestIncrease"
      >
        <EmailIcon class="icon--xs q-mr-xs email__icon" />
        Request Increase
        <PTooltip v-if="isRequestIncreaseButtonDisabled">Email Sent</PTooltip>
      </PBtn>
    </div>
    <div class="border-none row q-ma-none q-pa-none">
      <div class="q-mb-md col-6">
        <span class="q-mr-xxs">Status:</span>
        <span
          :class="
            formData.status_cd === 'active' ? 'text-info-50' : 'text-warning-70'
          "
        >
          {{ useChangeCase(formData.status_cd, "capitalCase").value || "-" }}
        </span>
      </div>
      <div class="q-mb-md col-6 text-right">
        <PBadge :label="taxLabel" :type="taxLabel" class="q-ml-xs" />
      </div>
    </div>

    <q-separator class="q-mb-xl q-mt-sm" />
    <div class="border-none q-ma-none q-pa-none">
      <div class="row q-col-gutter-lg">
        <div class="col-3">
          <PInput
            v-model.trim="formData.number"
            label="Invoice No."
            placeholder="Enter value"
            required
            :rules="[validateNumber]"
            :loading="validatingNumber"
            debounce="300"
            :disable="Boolean(formState.view)"
          />
        </div>
        <div class="col-3">
          <PVDateInput
            v-model="formData.date"
            label="Invoice Date"
            placeholder="DD-MM-YYYY"
            required
            :disable="Boolean(formState.view)"
            :min-date="invoiceDate"
            :max-date="invoiceDate"
          />
        </div>
        <div class="col-3">
          <PInput
            v-model.trim="formData.vehicle_number"
            label="Vehicle No."
            placeholder="Enter value"
            disable
          />
        </div>
        <div class="col-3">
          <PInput
            :model-value="formatPaymentTerms(formData?.po_info?.payment_terms)"
            label="Payment Terms"
            placeholder="Enter value"
            disable
          />
        </div>
        <div class="col-8">
          <div class="row">
            <div class="col-4">
              <span class="text-neutral-80">Customer</span>
            </div>
            <div class="col-8">
              <span class="text-neutral">
                {{ formData.po_info?.buyer_name || "-" }}
              </span>
            </div>
            <div class="col-4">
              <span class="text-neutral-80">GSTIN</span>
            </div>
            <div class="col-8">
              <span class="text-neutral">
                {{ formData.po_info?.buyer_gstin || "-" }}
              </span>
            </div>
            <div class="col-4">
              <span class="text-neutral-80">PO No.</span>
            </div>
            <div class="col-8">
              <span class="text-neutral">{{
                formData.po_info?.number || "-"
              }}</span>
            </div>
            <div class="col-4">
              <span class="text-neutral-80">Poshn PO No.</span>
            </div>
            <div class="col-8">
              <span class="text-neutral">
                {{ formData.po_info?.internal_number || "-" }}
              </span>
            </div>
          </div>
        </div>
        <div class="col-4">
          <p class="q-mb-none text-neutral-80">Remarks</p>
          <p class="q-mb-none">{{ formData.remarks || "-" }}</p>
        </div>
      </div>
    </div>
    <q-separator class="q-mb-xl q-mt-sm" />
    <div class="border-none q-ma-none q-pa-none">
      <div class="row q-col-gutter-lg">
        <div class="col-6 column">
          <PInput
            :model-value="billing_address"
            class="col"
            label="Bill To"
            placeholder="Enter customer's billing address"
            type="textarea"
            autogrow
            disable
            required
          />
        </div>
        <div class="col-6 column">
          <PInput
            :model-value="shipping_address"
            class="col"
            label="Ship To"
            placeholder="Enter customer's shipping address"
            type="textarea"
            autogrow
            disable
            required
          />
        </div>
        <div class="col-6 column">
          <PInput
            v-model="formData.dispatch_address"
            class="col"
            label="Dispatch Address"
            placeholder="Enter customer's Dispatch address"
            type="textarea"
            autogrow
            disable
            required
          />
        </div>
        <div class="col"></div>
        <div class="col-6">
          <div class="row">
            <div class="col-12">
              <span class="text-neutral">Files (for reference)</span>
            </div>
            <div class="col-3">
              <span class="text-neutral-80">Purchase Order</span>
            </div>

            <div class="col-9">
              <p v-if="loadingFiles" class="q-mb-none">Loading File...</p>
              <PMap
                v-else-if="files?.['Purchase Order']?.length"
                :files="files?.['Purchase Order']"
              />
              <span v-else>-</span>
            </div>

            <div class="col-3">
              <span class="text-neutral-80">Purchase Bill(s)</span>
            </div>
            <div class="col-9">
              <p v-if="loadingFiles" class="q-mb-none">Loading File...</p>
              <PMap
                v-else-if="purchaseBillFiles.length"
                :files="purchaseBillFiles"
              />
              <span v-else>-</span>
            </div>
            <div class="col-3">
              <span class="text-neutral-80">E-invoice</span>
            </div>

            <div class="col-9">
              <p v-if="loadingFiles" class="q-mb-none">Loading File...</p>
              <PMap
                v-else-if="files?.['E-Invoice']?.length"
                :files="files?.['E-Invoice']"
              />
              <span v-else>-</span>
            </div>
            <div class="col-3">
              <span class="text-neutral-80">POD file</span>
            </div>
            <div class="col-9">
              <p v-if="loadingFiles" class="q-mb-none">Loading File...</p>
              <PMap
                v-else-if="files?.['Proof of Delivery']?.length"
                :files="files?.['Proof of Delivery']"
              />
              <span v-else>-</span>
            </div>
            <div class="col-3">
              <span class="text-neutral-80">POD Status</span>
            </div>
            <div class="col-9">
              <PBadge
                v-if="formData?.latest_pod?.status"
                :label="
                  formData?.latest_pod?.status &&
                  useChangeCase(formData?.latest_pod?.status, 'capitalCase')
                    ?.value
                "
                :type="
                  formData?.latest_pod?.status &&
                  useChangeCase(formData?.latest_pod?.status, 'capitalCase')
                    ?.value
                "
              />
              <span v-else class="text-neutral-80">-</span>
            </div>
            <div v-if="isQCStage" class="col-3">
              <span class="text-neutral-80">QC file</span>
            </div>
            <div v-if="isQCStage" class="col-9">
              <p v-if="loadingFiles" class="q-mb-none">Loading File...</p>
              <PMap
                v-else-if="files?.['Quality Check']?.length"
                :files="files?.['Quality Check']"
              />
              <span v-else>-</span>
            </div>
          </div>
        </div>
        <div class="col-6">
          <div class="row">
            <div class="col-4">
              <span class="text-neutral-80">PB No.</span>
            </div>
            <div class="col-8">
              <span class="text-neutral text-wrap">
                {{ formData.bill?.number || "-" }}
              </span>
            </div>
            <div class="col-4">
              <span class="text-neutral-80">Poshn PB No.</span>
            </div>
            <div class="col-8">
              <span class="text-neutral">
                {{ formData.bill?.internal_number || "-" }}
              </span>
            </div>
            <div class="col-4">
              <span class="text-neutral-80">Additional PBs?</span>
            </div>
            <div class="col-8">
              <p class="q-mb-xxs text-neutral">
                {{ additionalPBs }}
              </p>
              <PBtn
                v-if="formData.bill?.number"
                dense
                flat
                text
                @click="previewPurchaseBill"
              >
                {{
                  loadingPbPreview
                    ? "Loading preview..."
                    : "Preview Purchase Bill"
                }}
              </PBtn>
              <PBtn
                v-if="
                  formData?.pod?.status_cd &&
                  formData?.pod?.status_cd !== 'cancelled'
                "
                dense
                flat
                text
                @click="previewPod"
              >
                {{
                  loadingPodPreview
                    ? "Loading preview..."
                    : "Preview Proof of Delivery"
                }}
              </PBtn>
              <PBtn
                v-if="
                  formData?.qualityCheckReport?._id &&
                  formData?.qualityCheckReport?.status_cd !== 'cancelled'
                "
                dense
                flat
                text
                @click="previewQualityCheck"
              >
                {{
                  loadingPodPreview
                    ? "Loading preview..."
                    : "Preview Quality Check Report"
                }}
              </PBtn>
            </div>
            <div v-if="isInvoiceStagePending" class="col-4">
              <span class="text-neutral-80">E-Way Bill No</span>
            </div>
            <div v-if="isInvoiceStagePending" class="col-8">
              <span class="text-neutral text-wrap">
                {{ formData.ewayBillAgainstPurchaseBill?.number || "-" }}
              </span>
            </div>
            <div v-if="isInvoiceStagePending" class="col-4">
              <span class="text-neutral-80">E-Way File</span>
            </div>
            <div v-if="isInvoiceStagePending" class="col-7">
              <p v-if="loadingFiles" class="q-mb-none">Loading File...</p>
              <PMap
                v-else-if="files?.['E-Way Bill']?.length"
                :files="files?.['E-Way Bill']"
              />
              <span v-else>-</span>
              <div>
                <PBtn
                  v-if="formData.ewayBillAgainstPurchaseBill?.number"
                  dense
                  flat
                  text
                  @click="previewEWayBill"
                >
                  {{
                    loadingEWayBillPreview
                      ? "Loading preview..."
                      : "Preview E-way Bill"
                  }}
                </PBtn>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <q-separator v-if="!formState.view" class="q-mb-xl q-mt-sm" />
    <div v-if="!formState.view" class="overflow-hidden">
      <div class="row">
        <div class="col-12">
          <PFile
            v-model="file"
            label="Attach Proof of Document (E-invoice)"
            help="Allowed file types are PDFs, images, excel, csv"
            required
          />
          <PFile
            v-if="file"
            v-model="file"
            class="q-mt-xxs"
            variant="re-upload"
          />
        </div>
      </div>
    </div>
    <q-separator class="q-mb-xl q-mt-sm" />
    <div class="overflow-hidden">
      <div class="row justify-between">
        <legend class="q-mb-sm text-neutral text-weight-medium text-md">
          Items
        </legend>
        <div v-if="formData.lineitems.length">
          <span class="text-sm">Sales Margin:</span>
          <PBadge
            v-for="marginType in marginTypes"
            :key="marginType"
            :label="marginType"
            :type="marginType"
            class="q-ml-xs"
          />
        </div>
      </div>

      <q-separator v-if="formData?.lineitems?.length" />

      <PTable
        v-if="formData.lineitems.length"
        :columns="productListColumns"
        :pagination="{
          rowsPerPage: 25,
        }"
        rows-per-page-label=" "
        :rows-per-page-options="[]"
        :rows="formData.lineitems"
        :style="{ margin: '0 -15px', padding: 0 }"
        class="text-xs"
        dense
        flat
        wrap-cells
      >
        <template #header="headerProps">
          <q-tr :props="headerProps">
            <q-th
              v-for="col in headerProps.cols"
              :key="col.name"
              :props="headerProps"
              class="text-neutral-70"
              auto-width
            >
              {{ col.label }}
            </q-th>
          </q-tr>
        </template>
        <template #body="props">
          <q-tr :props="props" :class="getMarginClasses(props.row)">
            <q-td v-for="col in props.cols" :key="col.name" :props="props">
              <ItemNameCell
                v-if="col.name === 'name'"
                :col="col"
                :table-props="props"
              />

              <ItemQuantityCell
                v-else-if="col.name === 'qty'"
                :col="col"
                :table-props="props"
              />

              <ItemRateCell
                v-else-if="col.name === 'rate'"
                :col="col"
                :table-props="props"
              />

              <ItemAmountCell
                v-else-if="col.name === 'amount'"
                :col="col"
                :table-props="props"
              />

              <div
                v-else-if="
                  col.name === 'gst_exempted' && props.row.gst_exempted
                "
              >
                <CheckMarkIcon class="icon--sm text-primary" />
              </div>

              <PEditable
                v-else
                :model-value="col.value"
                :type="col.type"
                disable
              />
            </q-td>
          </q-tr>
        </template>
      </PTable>
      <span v-else class="text-neutral-50">No Items</span>
    </div>
    <q-separator class="q-mb-xl q-mt-sm" />
    <div class="border-none q-ma-none q-pa-none">
      <div class="row q-col-gutter-lg">
        <div class="col-6">
          <p class="text-sm">
            <strong>Note:</strong>
            <span>
              Overall min margin expectation for this invoice is
              <strong>{{ overallMargin?.expected }}%</strong>. <br />
              Please talk to category lead to verify and update this expectation
              in case of discripency.
            </span>
          </p>
          <DealSettingMessage
            v-if="!dealSettingExist"
            v-bind="dealSettingMessageProps"
          />
        </div>
        <div class="col-6">
          <div class="row q-col-gutter-lg q-mb-md items-center">
            <div class="col-6">
              <span class="text-neutral"> Overall Margin </span>
            </div>
            <div class="col-6">
              <div class="text-right" :class="overallMarginClasses">
                {{ overallMargin?.sales + "%" }}
              </div>
            </div>
          </div>
          <div class="row q-col-gutter-lg q-mb-md">
            <div class="col-6">
              <p class="text-neutral q-mb-none">Total Amount</p>
              <span class="text-xs text-neutral-70">Excludes Tax</span>
            </div>
            <div class="col-6">
              <div class="text-right">
                {{
                  numberFormat({
                    number: getTotalAmountWithoutTax(),
                    style: "currency",
                  })
                }}
              </div>
            </div>
          </div>
          <div class="row q-col-gutter-lg q-mb-md">
            <div class="col-6">
              <p class="q-mb-none">Total Tax</p>
              <span class="text-xs text-neutral-70"> GST + CESS </span>
            </div>
            <div class="col-6">
              <div class="text-right">
                {{
                  numberFormat({
                    number: formData.total_tax,
                    style: "currency",
                  })
                }}
              </div>
            </div>
          </div>

          <ServiceItemList
            v-if="formData?.vas_items?.length"
            :vas-items="formData.vas_items"
            class="q-mb-md"
            view-mode
          />

          <div class="row q-col-gutter-lg q-mb-md">
            <div class="col-6">
              <p class="text-neutral q-mb-none">Grand Total</p>
              <span class="text-xs text-neutral-70">Includes Tax</span>
            </div>
            <div class="col-6">
              <div class="text-right">
                {{
                  numberFormat({
                    number: formData.total_amount,
                    style: "currency",
                  })
                }}
              </div>
            </div>
          </div>

          <div v-if="formData.tcs" class="row q-col-gutter-lg q-mb-md">
            <div class="col-6">
              <span class="text-neutral">
                TCS ({{ formData.tcs.percentage }}%)
              </span>
            </div>
            <div class="col-6">
              <div class="text-right">
                {{
                  numberFormat({
                    number: formData.tcs.amount,
                    style: "currency",
                  })
                }}
              </div>
            </div>
          </div>
          <div v-if="formData.tcs" class="row q-col-gutter-lg">
            <div class="col-6">
              <span class="text-neutral"> Net Receivable </span>
            </div>
            <div class="col-6">
              <div class="text-right">
                {{
                  numberFormat({
                    number: netReceivable,
                    style: "currency",
                  })
                }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </PForm>
</template>

<script setup>
import { useQuasar } from "quasar";

import { useChangeCase } from "@vueuse/integrations/useChangeCase";

import CheckMarkIcon from "assets/icons/checkmark.svg";
import AiStarsIcon from "assets/icons/ai-star-outline.svg";
import AutoGenIconUrl from "assets/icons/auto-gen.svg?url";

import * as fileApi from "src/api/file";

import { notify } from "assets/scripts/utilities/api-clients";

import PConfirmDialog from "src/components/PConfirmDialog.vue";
import PDialogForm from "src/components/PDialogForm.vue";

import { useFilter } from "src/composables/filter";

import invoiceFormModel from "src/modules/accounts/models/invoiceForm";

import {
  CONFIRMATION_DIALOG_TEXT_MAPPING,
  POD_REJECTION_REASONS,
  QC_REJECTION_REASONS,
  REJECT_POD_PROPS,
  REJECT_QC_PROPS,
  STAGE_TO_CTA_PROPS_MAPPINGS,
} from "../../constants";
import { PRODUCT_MARGIN } from "assets/scripts/utilities/constants";

import {
  formatPaymentTerms,
  numberFormat,
  roundOff,
} from "assets/scripts/utilities/helpers";
import { useCategories } from "src/composables/categories";

import ExclamationIcon from "assets/icons/exclamation-round-color.svg";
import EmailIcon from "assets/icons/email.svg";
import CreditLimitIncreaseRequestDialogue from "../CreditLimitIncreaseRequestDialogue.vue";

const $q = useQuasar();

const loadingPbPreview = ref(false);
const { purchaseBill } = storeToRefs(usePurchaseBillStore());
const { getPurchaseBill, togglePurchaseBillForm } = usePurchaseBillStore();
const { getDebitNotesByPB } = useDebitNoteStore();

const currentDate = new Date();

const validatingNumber = ref(false);
const isDuplicateInvoiceNumber = ref(false);

const previewPurchaseBill = async () => {
  try {
    const { bill_id } = unref(formData);
    let response;

    if (bill_id) {
      loadingPbPreview.value = true;

      response = await Promise.all([
        getPurchaseBill(bill_id),
        getDebitNotesByPB(bill_id),
      ]);

      loadingPbPreview.value = false;

      if (bill_id === unref(purchaseBill)?._id || response[0].data?.data?._id) {
        togglePurchaseBillForm({
          level: 0,
          open: true,
          edit: true,
          view: false,
          preview: true,
        });
      }
    }
  } catch (error) {
    console.error(error);

    loadingPbPreview.value = false;
  }
};

const loadingEWayBillPreview = ref(false);

const { getEWayBill, toggleEWayBillRequestForm } = useEWayBillStore();

const previewEWayBill = async () => {
  try {
    const { ewayBillAgainstPurchaseBill } = unref(formData);

    if (!ewayBillAgainstPurchaseBill) {
      return;
    }

    loadingEWayBillPreview.value = true;

    await getEWayBill({ id: ewayBillAgainstPurchaseBill?._id });

    loadingEWayBillPreview.value = false;

    toggleEWayBillRequestForm({
      edit: true,
      level: 0,
      open: true,
      view: true,
      preview: true,
      props: {
        requestAgainst: "purchase_bill",
      },
    });
  } catch (error) {
    console.error(error);

    loadingEWayBillPreview.value = false;
  }
};

const loadingPodPreview = ref(false);

const shipping_address = computed(() => {
  if (unref(formData)?.shipping_address) {
    const shop_name = unref(formData)?.shipping_address_info?.shop_name
      ? `${unref(formData)?.shipping_address_info?.shop_name}\n`
      : "";

    return `${shop_name}${unref(formData)?.shipping_address}`;
  }

  return "";
});

const billing_address = computed(() => {
  if (unref(formData)?.billing_address) {
    const shop_name = unref(formData)?.billing_address_info?.shop_name
      ? `${unref(formData)?.billing_address_info?.shop_name}\n`
      : "";

    return `${shop_name}${unref(formData)?.billing_address}`;
  }

  return "";
});

const { getPod, togglePodForm } = usePodStore();
const { pod } = storeToRefs(usePodStore());

const previewPod = async () => {
  try {
    const podId = unref(formData)?.pod?._id;

    let response;

    if (podId) {
      loadingPodPreview.value = true;

      response = await getPod({ id: podId });

      loadingPodPreview.value = false;

      if (podId === unref(pod)?._id || response?.data?.data?._id) {
        togglePodForm({
          edit: true,
          level: 0,
          open: true,
          preview: true,
        });
      }
    }
  } catch (error) {
    console.error(error);

    loadingPodPreview.value = false;
  }
};

const loadingQCPreview = ref(false);
const { getQualityCheck, toggleQualityCheckForm } = useQualityCheckStore();

const previewQualityCheck = async () => {
  try {
    const qcId = unref(formData)?.qualityCheckReport?._id;

    if (!qcId) {
      return;
    }

    loadingQCPreview.value = true;

    await getQualityCheck({ _id: qcId });

    loadingQCPreview.value = false;

    toggleQualityCheckForm({
      edit: true,
      level: 0,
      open: true,
      view: true,
    });
  } catch (error) {
    console.log(error);

    loadingQCPreview.value = false;
  }
};

const pForm = ref(null);
const qcInvoiceStages = new Set(["QC Received", "QC Accepted", "QC Rejected"]);

const isQCStage = computed(() => {
  return qcInvoiceStages.has(unref(formData).stage);
});

const { formState, invoice, isAutoGenerationFailed } =
  storeToRefs(useInvoiceStore());
const {
  autoGenerateInvoice,
  getInvoices,
  postInvoice,
  putInvoice,
  toggleInvoiceForm,
  toggleInvoiceAutoGenForm,
  isDuplicate,
  acceptInvoice,
} = useInvoiceStore();

const { formData, isLoading, closeForm, resetForm, submitForm } = useForm({
  formModel: invoiceFormModel(),
  entity: invoice,
  formRef: pForm,
  isForm: formState,
  post: postInvoice,
  put: putInvoice,
  toggleForm: toggleInvoiceForm,
});

const additionalPBs = computed(() => {
  return unref(formData)?.addl_bills?.length
    ? unref(formData)
        ?.addl_bills?.map((bill) => bill.number)
        .join(", ")
    : "No";
});

const serviceFees = computed(() => {
  return (
    unref(formData)?.vas_items?.reduce((acc, item) => {
      return acc + item.amount;
    }, 0) || 0
  );
});

const netReceivable = computed(() => {
  return (
    unref(formData).total_amount +
    (unref(formData).tcs?.amount ?? 0) +
    unref(serviceFees)
  );
});

const isCompleting = ref(false);
const isRejecting = ref(false);

const isAccepting = ref(false);

const handleCompleteActionConfirmation = () => {
  $q.dialog({
    component: PConfirmDialog,
    componentProps: {
      labelClass: "text-center q-ma-xl rounded-borders",
      text: "Are you sure you want to mark this invoice stage as Complete ?",
      onConfirm: () => handleCompleteAction(),
    },
  });
};

const handleCompleteAction = async () => {
  try {
    isCompleting.value = true;

    await unref(formState).props.complete(unref(formData));

    isCompleting.value = false;

    closeForm();
  } catch (error) {
    console.error(error);

    isCompleting.value = false;
  }
};

const handleAcceptAction = async (acceptConfirmationDialog, remarks) => {
  try {
    isAccepting.value = true;

    const nextStageMapping = {
      pod_received: "pod_accepted",
      "POD Received": "POD Accepted",
      qc_received: "qc_accepted",
      "QC Received": "QC Accepted",
    };
    const payload = {
      _id: unref(invoice)._id,
      remarks: unref(remarks),
      stage_cd: nextStageMapping[unref(invoice).stage_cd],
      stage: nextStageMapping[unref(invoice).stage],
    };

    await acceptInvoice(payload);

    isAccepting.value = false;

    acceptConfirmationDialog?.hide();

    closeForm();
  } catch (error) {
    console.error(error);

    isAccepting.value = false;
  }
};

const handleAcceptConfirmation = () => {
  const acceptConfirmationDialog = $q.dialog({
    component: PDialogForm,
    componentProps: {
      ...CONFIRMATION_DIALOG_TEXT_MAPPING[unref(formData).stage]["ACCEPT"],
      cancellable: false,
      loading: isAccepting,
      label:
        "Please make sure you have generated associated credit and debit notes before proceeding.",
      textClass: "text-md text-neutral text-weight-bold",
      labelClass: "text-md text-neutral",
      form: {
        input: {
          type: "textarea",
          autogrow: true,
          placeholder: "Enter Reason",
          label: "Reason/remarks",
          labelClasses: "text-sm text-neutral text-weight-bold",
          help: "Do you want to add any additional reason or remarks?",
          helpClasses: "text-sm text-neutral-70 q-mt-none",
        },
      },
      onConfirm: (remarks) => {
        handleAcceptAction(acceptConfirmationDialog, remarks);
      },
    },
  });
};

const handleRejectActionConfirmation = (rejectDialog, rejectReason) => {
  $q.dialog({
    component: PConfirmDialog,
    componentProps: {
      ...CONFIRMATION_DIALOG_TEXT_MAPPING[unref(formData).stage]["REJECT"],
      labelClass: "text-center q-ma-xl rounded-borders",
      closable: true,
      onConfirm: () => {
        handleRejectAction(rejectDialog, rejectReason);
      },
    },
  });
};

const handleRejectAction = async (rejectDialog, rejectReason) => {
  try {
    isRejecting.value = true;

    unref(formData).reason = rejectReason;

    const payloadMapping = {
      pod_received: {
        _id: unref(formData)?.latest_pod._id,
      },
      qc_received: {
        _id: unref(formData)?.qualityCheckReport?._id,
      },
    };
    const data = {
      ...payloadMapping[unref(formData).stage_cd],
      reason: unref(rejectReason),
    };
    const payload = {
      pod_received: {
        pod: data,
        path: "account",
      },
      qc_received: {
        qualityCheck: data,
        user_role: "accounts",
      },
    };

    await unref(formState).props.cancel(payload[unref(formData).stage_cd]);
  } catch (error) {
    console.error(error);
  } finally {
    isRejecting.value = false;

    rejectDialog?.hide();

    closeForm(true);
  }
};

const handleRejectConfirm = () => {
  if (!unref(formData).pod) {
    return notify({
      message: "No Active POD's found for this invoice",
      type: "negative",
    });
  }

  const stageToReasonsMapping = {
    "POD Received": POD_REJECTION_REASONS,
    "QC Received": QC_REJECTION_REASONS,
  };
  const stageToPropsMappings = {
    "POD Received": {
      title: `Reject POD against Invoice: ${unref(invoice).number}`,
      ...REJECT_POD_PROPS,
    },
    "QC Received": {
      title: `Reject QC against Invoice: ${unref(invoice).number}`,
      ...REJECT_QC_PROPS,
    },
  };

  const rejectDialog = $q.dialog({
    component: PDialogForm,
    componentProps: {
      ...stageToPropsMappings[unref(formData).stage],
      submitLabel: "Submit",
      form: {
        dropdown: {
          ...stageToPropsMappings[unref(formData).stage].form.dropdown,
          "option-label": "name",
          "option-value": "_id",
          "input-debounce": "300",
          "virtual-scroll-slice-size": "25",
          "virtual-scroll-item-size": "53",

          label: "Reason to Reject",
          labelClasses: "text-sm text-neutral",

          helpClasses: "text-sm text-neutral-70 q-mt-none",
          filter: useFilter({
            entities: stageToReasonsMapping[unref(formData).stage],
          }),
          validations: {
            label: "Rejection reason",
            required: true,
          },
          name: stageToReasonsMapping[unref(formData).stage]?.[0]?.name,
          _id: stageToReasonsMapping[unref(formData).stage]?.[0]?._id,
        },
      },
      cancellable: false,
      loading: isRejecting,
      onConfirm: (rejectReason) => {
        handleRejectActionConfirmation(rejectDialog, rejectReason);
      },
    },
  });
};

const submitButton = computed(() => {
  const stageCtaMappings = {
    "QC Received": {
      label: "Accept QC",
      loading: unref(isAccepting),
      type: "button",
      onClick: handleAcceptConfirmation,
    },
    "POD Received": {
      label: "Accept proof of delivery",
      loading: unref(isAccepting),
      type: "button",
      onClick: handleAcceptConfirmation,
      disable: !unref(dealSettingExist),
    },
    "POD Rejected": {
      label: "Mark as complete",
      loading: unref(isCompleting),
      type: "button",
      onClick: handleCompleteActionConfirmation,
      disable: !unref(formState)?.props?.isCompletable?.(unref(formData)),
    },
  };

  if (stageCtaMappings[unref(formData).stage]) {
    return stageCtaMappings[unref(formData).stage];
  }

  if (
    ["POD Pending"].includes(unref(formData).stage) ||
    unref(formState).view
  ) {
    return {};
  }

  return {
    loading: unref(isLoading),
    disable:
      unref(validatingNumber) ||
      unref(isDuplicateInvoiceNumber) ||
      !["In Progress", "Not Started"].includes(unref(formData).stage),
  };
});

const rejectButton = computed(() => {
  const commonProps = {
    outline: true,
  };

  if (unref(formState)?.props?.isRejectable?.(unref(formData))) {
    return {
      ...STAGE_TO_CTA_PROPS_MAPPINGS[unref(formData).stage],

      color: "error-70",
      // valid types ["reset", "submit", "button"]
      type: "button",
      onClick: () => handleRejectConfirm(),
      disable: !unref(formState)?.props?.isRejectable?.(unref(formData)),
      ...commonProps,
    };
  }

  return undefined;
});

let isAutoGenerating = ref(false);

const onConfirmAutoGeneration = async () => {
  try {
    isAutoGenerating.value = true;

    await autoGenerateInvoice({ invoice: unref(formData), isGstExempted });

    closeForm(true);
  } catch (error) {
    console.error(error);
  } finally {
    isAutoGenerating.value = false;
  }
};

const confirmAutoGeneration = () => {
  $q.dialog({
    component: PConfirmDialog,
    componentProps: {
      labelClass: "text-center q-ma-xl rounded-borders",
      text: "Are you sure want to proceed?",
      onConfirm: onConfirmAutoGeneration,
    },
  });
};

const initAutoGenerate = () => {
  if (unref(taxLabel) === "Taxable") {
    toggleInvoiceAutoGenForm({ open: true });
  } else {
    confirmAutoGeneration();
  }
};

const oneClickGenerateButton = computed(() => {
  return {
    disable:
      unref(loadingPortfolio) ||
      unref(shortFallAmount) > 0 ||
      unref(validatingNumber) ||
      unref(isDuplicateInvoiceNumber),
    icon: `img:${AutoGenIconUrl}`,
    label: "1-Click generate",
    loading: unref(isAutoGenerating),
    outline: true,
    onClick: initAutoGenerate,
    type: "button",
  };
});

const secondaryCta = computed(() => {
  if (unref(formState)?.props?.isRejectable?.(unref(formData))) {
    return unref(rejectButton);
  }

  if (unref(formState).view) {
    return {};
  }

  if (unref(formData).stage === "Not Started") {
    return unref(oneClickGenerateButton);
  }

  return { visible: true };
});

const files = ref({});
const loadingFiles = ref(false);

const validateNumber = async (value) => {
  try {
    validatingNumber.value = true;

    isDuplicateInvoiceNumber.value = false;

    const payload = { number: value, _id: unref(formData)._id };

    await isDuplicate(payload);

    validatingNumber.value = false;

    return true;
  } catch (error) {
    validatingNumber.value = false;

    isDuplicateInvoiceNumber.value = true;

    if (error?.response?.status >= 400 && error?.response?.status < 500) {
      return "Invoice number already exists";
    }

    return true;
  }
};

const getFiles = async () => {
  try {
    loadingFiles.value = true;

    const { po_id, bill_id, _id } = unref(formData);
    const status_cd = "active";
    const fileGetters = [];

    files.value = {};

    if (po_id) {
      fileGetters.push(
        fileApi.index({
          params: {
            po_id,
            fileType: "Purchase Order",
            status_cd,
          },
        }),
      );
    }

    const bill_ids = unref(formData)?.addl_bills?.length
      ? unref(formData)
          ?.addl_bills?.map((bill) => bill._id)
          .join(",") +
        "," +
        bill_id
      : bill_id;

    if (bill_ids) {
      fileGetters.push(
        fileApi.index({
          params: {
            bill_id: bill_ids,
            status_cd,
          },
        }),
      );
    }

    if (_id) {
      fileGetters.push(
        fileApi.index({
          params: {
            invc_id: _id,
            fileType: "E-Invoice",
            status_cd,
          },
        }),
      );
    }

    if (unref(formData)?.latest_pod) {
      fileGetters.push(
        fileApi.index({
          params: {
            pod_id: unref(formData)?.latest_pod?._id,
            status_cd,
          },
        }),
      );
    }

    if (unref(formData)?.ewayBillAgainstPurchaseBill?._id) {
      fileGetters.push(
        fileApi.index({
          params: {
            ewaybill_id: unref(formData)?.ewayBillAgainstPurchaseBill?._id,
            status_cd,
            fileType: "E-Way Bill",
          },
        }),
      );
    }

    if (unref(formData)?.qualityCheckReport?._id) {
      fileGetters.push(
        fileApi.index({
          params: {
            qc_id: unref(formData)?.qualityCheckReport?._id,
            status_cd,
          },
        }),
      );
    }

    const response = await Promise.all(fileGetters);

    for (const entity of response) {
      let entityFiles = entity?.data?.data?.data;

      if (entityFiles?.length) {
        unref(files)[entityFiles[0]?.file_type] = entityFiles;
      }
    }
  } catch (error) {
    console.error(error);
  } finally {
    loadingFiles.value = false;
  }
};

const { isRightDrawerOpen } = storeToRefs(useAppStore());

const purchaseOrder = computed(() => {
  return unref(invoice)?.po_info;
});

const { fetchCategories, categories } = useCategories({
  searchCategories: ["Service", "Discount"],
});

const {
  marginTypes,
  overallMargin,
  overallMarginClasses,
  getConvertedRate,
  getMarginClasses,
  getSalesMargin,
  getTotalAmountWithoutTax,
} = useProductList({
  formData,
  purchaseOrder,
  excludedCategories: categories,
});

const productListColumns = computed(() => {
  let columns = [
    {
      align: "left",
      name: "name",
      label: "Product Name",
      field: "name",
    },
    {
      align: "left",
      name: "qty",
      label: "Qty",
      field: "qty",
    },
    {
      align: "right",
      name: "rate",
      label: "Rate",
      field: "rate",
      type: "currency",
    },
    {
      align: "right",
      name: "amount",
      label: "Total",
      field: "amount",
      type: "currency",
    },
    {
      align: "left",
      name: "sales_margin",
      label: "Sales Margin",
      field: getSalesMargin,
      format: (salesMargin = 0) => `${salesMargin}%`,
    },
    {
      align: "left",
      name: "margin_expectation",
      label: "Expected Margin (Min)",
      field: (row) =>
        row?.margin_expectation?.min || PRODUCT_MARGIN.default.min,
      format: (minMarginExpectation) =>
        minMarginExpectation ? `${minMarginExpectation}%` : "-",
    },
    {
      align: "right",
      name: "converted_rate",
      label: "Conv. PB Rate (Excl. Tax)",
      field: getConvertedRate,
      format: (convertedRate) => convertedRate || "-",
      type: "currency",
    },
  ];

  return columns;
});

const { getCounts } = useAccountStore();
const { file, uploadFile } = useFile(formData);

const isGstExempted = computed(() => {
  return (
    !unref(formData).total_tax &&
    unref(formData)?.lineitems?.findIndex(
      (line_item) => !line_item.gst_exempted,
    ) === -1
  );
});

const taxLabel = computed(() => {
  if (unref(isGstExempted)) {
    return "GST Exempted";
  }

  return "Taxable";
});

const onSubmit = async () => {
  const submission = await submitForm(false);

  isLoading.value = true;

  await uploadFile({ type: "E-Invoice", showMessage: false });

  await Promise.all([getCounts(), getInvoices()]);

  isLoading.value = false;

  closeForm(true);

  notify({
    type: "positive",
    message: submission?.data?.message,
  });
};

const { getUserPortfolio } = useUserStore();

const loadingPortfolio = ref(true);

const shortFallAmount = ref(0);

const requestRelatedDetails = ref({
  userPortfolio: null,
  invoice: null,
  customer: null,
  doc_id: null,
});

const loadCustomerPortfolio = async () => {
  try {
    loadingPortfolio.value = true;

    const customerId =
      unref(formData)?.po_info?.buyer?._id || unref(formData)?.po_info?.buyer;

    if (customerId) {
      const userPortfolio = await getUserPortfolio(customerId);

      const {
        unused_credits_receivable_amount = 0,
        outstanding_receivable_amount = 0,
        credit_limit = 1,
      } = userPortfolio;

      let availableCreditAmount = 0;

      if (+credit_limit === 0) {
        availableCreditAmount = Number.POSITIVE_INFINITY;
      } else {
        availableCreditAmount =
          +credit_limit +
          +unused_credits_receivable_amount -
          +outstanding_receivable_amount;
      }

      const totalInvoiceAmount =
        +unref(formData).total_amount + (unref(formData).tcs?.amount ?? 0);

      const remainingAmount = roundOff(
        totalInvoiceAmount - availableCreditAmount,
      );

      if (remainingAmount > 0) {
        shortFallAmount.value = remainingAmount;
      }

      requestRelatedDetails.value.invoice = {
        totalInvoiceAmount,
        _id: unref(formData)._id,
        created_by: unref(formData)?.created_by?._id,
      };

      requestRelatedDetails.value.customer = {
        _id: customerId,
        name: unref(formData).po_info?.buyer_name,
        gstin: unref(formData).po_info?.buyer_gstin,
      };

      requestRelatedDetails.value.doc_id = unref(formData)._id;

      requestRelatedDetails.value.userPortfolio = {
        ...userPortfolio,
        availableCreditAmount,
        shortFallAmount,
      };
    }
  } catch (error) {
    console.error(error);
  } finally {
    loadingPortfolio.value = false;
  }
};

const dealSettingMessageProps = computed(() => {
  return {
    buyerName: unref(formData).po_info?.buyer_name || "",
    sellerName: unref(formData).bill?.seller?.shop_name || "",
    buyerGstin: unref(formData)?.po_info?.buyer_gstin || "",
    sellerGstin: unref(formData)?.bill?.seller?.gstin || "",
  };
});

const { fetchDealSettings, dealSettingExist } = useDealSettingsExist();

watchEffect(() => {
  if (unref(isRightDrawerOpen)) {
    isAutoGenerationFailed.value = false;

    shortFallAmount.value = 0;

    getFiles();

    fetchCategories();

    if (unref(formData).stage_cd === "pod_received") {
      fetchDealSettings({
        buyerId: unref(formData)?.po_info?.buyer?._id,
        sellerId: unref(formData)?.bill?.seller?._id,
      });
    }

    if (unref(formData).stage_cd === "pending") {
      loadCustomerPortfolio();
    }
  }
});

const isInvoiceStagePending = computed(() => {
  return unref(formData).stage_cd === "pending";
});

const showAiSuggestButton = computed(() => {
  return (
    unref(formData)?.pod?.ai_verification?.reason &&
    ["pod_received", "pod_rejected", "pod_accepted"].includes(
      unref(formData).stage_cd,
    )
  );
});

const invoiceDate = computed(() => {
  if (unref(formState).view) {
    return new Date(unref(formData).date);
  }

  return currentDate;
});

const purchaseBillFiles = computed(() => {
  const filesToShow = [];

  if (unref(files)?.["Vendor Purchase Order"]) {
    filesToShow.push(...unref(files)["Vendor Purchase Order"]);
  }

  if (unref(files)?.["Purchase Bill"]) {
    filesToShow.push(...unref(files)["Purchase Bill"]);
  }

  return filesToShow;
});

const creditLimitIncreaseRequested = ref(false);

const isRequestIncreaseButtonDisabled = computed(
  () =>
    unref(creditLimitIncreaseRequested) ||
    unref(formData)?.credit_limit_increase_request?.is_requested,
);

const disableCreditLimitIncreaseRequest = (value) => {
  creditLimitIncreaseRequested.value = !!value;
};

const handleRequestIncrease = () => {
  $q.dialog({
    component: CreditLimitIncreaseRequestDialogue,
    componentProps: {
      details: unref(requestRelatedDetails),
      disableCreditLimitIncreaseRequest,
    },
  });
};
</script>
